import { create } from "zustand";
import { useAuthStore } from "./auth";

type EventCallback = (payload: any) => void;

interface WsState {
  ws: WebSocket | null;
  connected: boolean;
  intentionalDisconnect: boolean;
  subscribedSessions: Set<string>;
  connect: () => void;
  disconnect: () => void;
  send: (payload: any) => void;

  // Session event subscriptions
  sessionListeners: Map<string, Set<EventCallback>>;
  subscribeSession: (sessionId: string, cb: EventCallback) => () => void;
  globalListeners: Set<EventCallback>;
  subscribeAll: (cb: EventCallback) => () => void;

  // Auth event subscriptions
  authListeners: Map<string, EventCallback>;
  subscribeAuth: (providerId: string, cb: EventCallback) => () => void;
}

export const useWsStore = create<WsState>((set, get) => ({
  ws: null,
  connected: false,
  intentionalDisconnect: false,
  subscribedSessions: new Set(),
  sessionListeners: new Map(),
  globalListeners: new Set(),
  authListeners: new Map(),

  connect: () => {
    // Prevent duplicate connections
    const existing = get().ws;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const { accessToken } = useAuthStore.getState();
    if (!accessToken) return;

    set({ intentionalDisconnect: false });

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws/user?token=${encodeURIComponent(accessToken)}`;
    const ws = new WebSocket(url);

    // Client-side heartbeat to keep CDN alive
    const heartbeatInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ msg_id: crypto.randomUUID(), payload: { type: "Ping" } }));
      }
    }, 25000);

    ws.onopen = () => {
      set({ connected: true });
      console.log("[WS] connected");

      // Re-subscribe all tracked sessions after reconnect
      const sessions = get().subscribedSessions;
      sessions.forEach((sid) => {
        ws.send(
          JSON.stringify({
            msg_id: crypto.randomUUID(),
            payload: {
              type: "SubscribeSession",
              data: { session_id: sid },
            },
          }),
        );
      });
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        const payload = msg.payload;
        if (!payload) return;
        const type = payload.type;
        const data = payload.data;

        if (type === "Event" && data?.session_id) {
          // Route to session listeners
          const { sessionListeners, globalListeners } = get();
          const eventKey = data.event ? (typeof data.event === "string" ? data.event : Object.keys(data.event)[0]) : "?";
          const hasSub = sessionListeners.has(data.session_id);
          const subCount = sessionListeners.get(data.session_id)?.size ?? 0;
          console.log(`[WS] Event ${eventKey} for session ${data.session_id.slice(0,8)} → subs=${subCount} hasSub=${hasSub}`);
          const cbs = sessionListeners.get(data.session_id);
          if (cbs) cbs.forEach((cb) => cb(data));
          globalListeners.forEach((cb) => cb(data));
        } else if (type === "SessionTitleUpdate" && data?.session_id) {
          const { sessionListeners, globalListeners } = get();
          const cbs = sessionListeners.get(data.session_id);
          if (cbs) cbs.forEach((cb) => cb({ ...data, _type: "title_update" }));
          globalListeners.forEach((cb) =>
            cb({ ...data, _type: "title_update" }),
          );
        } else if (type === "AuthEvent" && data?.provider_id) {
          const { authListeners } = get();
          const cb = authListeners.get(data.provider_id);
          if (cb) cb(data);
        } else if (type === "MessageAck" && data?.msg_id) {
          // ✓/✓✓ delivery confirmation
          const { globalListeners } = get();
          globalListeners.forEach((cb) =>
            cb({ _type: "message_ack", msg_id: data.msg_id, status: data.status }),
          );
        } else if (type === "Result") {
          const { globalListeners } = get();
          globalListeners.forEach((cb) =>
            cb({ ...data, _type: "result", msg_id: msg.msg_id }),
          );
        } else if (type === "Error" && data?.code === 401) {
          // Auth failure — do not reconnect, log out
          console.warn("[WS] 401 auth error, logging out");
          set({ intentionalDisconnect: true });
          ws.close();
          useAuthStore.getState().logout();
        }
      } catch {
        /* ignore */
      }
    };

    ws.onclose = (ev) => {
      clearInterval(heartbeatInterval);
      set({ connected: false, ws: null });

      // 1008 = Policy Violation — treat as auth failure
      if (ev.code === 1008) {
        console.warn("[WS] closed with 1008 (auth), logging out");
        set({ intentionalDisconnect: true });
        useAuthStore.getState().logout();
        return;
      }

      if (get().intentionalDisconnect) {
        console.log("[WS] intentionally disconnected");
        return;
      }

      console.log("[WS] disconnected, reconnecting in 3s...");
      setTimeout(() => get().connect(), 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    set({ ws });
  },

  disconnect: () => {
    set({ intentionalDisconnect: true });
    const { ws } = get();
    if (ws) ws.close();
    set({ ws: null, connected: false });
  },

  send: (payload) => {
    const { ws } = get();
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ msg_id: crypto.randomUUID(), payload }));
    }
  },

  subscribeSession: (sessionId, cb) => {
    const { sessionListeners, subscribedSessions } = get();
    if (!sessionListeners.has(sessionId)) {
      sessionListeners.set(sessionId, new Set());
    }
    sessionListeners.get(sessionId)!.add(cb);

    // Track session for re-subscription on reconnect
    subscribedSessions.add(sessionId);

    // Tell server to subscribe
    get().send({
      type: "SubscribeSession",
      data: { session_id: sessionId },
    });

    return () => {
      const listeners = sessionListeners.get(sessionId);
      listeners?.delete(cb);
      // Only unsubscribe from server if no more listeners for this session
      if (!listeners || listeners.size === 0) {
        subscribedSessions.delete(sessionId);
        get().send({
          type: "UnsubscribeSession",
          data: { session_id: sessionId },
        });
      }
    };
  },

  subscribeAll: (cb) => {
    get().globalListeners.add(cb);
    return () => {
      get().globalListeners.delete(cb);
    };
  },

  subscribeAuth: (providerId, cb) => {
    get().authListeners.set(providerId, cb);
    return () => {
      get().authListeners.delete(providerId);
    };
  },
}));
